<template>
  <modal :name="modalName" :width="520">
    <div>
      <header class="modal-header center-md center-xs py15 px65 h3 sans-serif weight-700 bg-cl-secondary flex">
        <span>{{ $t('Pay for order #') + order.connection_order_id}}</span>
        <i slot="close" class="modal-close material-icons p15 cl-bg-tertiary" @click="close" data-testid="closeModalButton">
          close
        </i>
      </header>
      <div class="modal-content px30 pt0">
        <template v-if="changeMethodMode">
          <pay-order-modal-change-methods :selectedMethod="selectedMethod" @changeMethod="changeMethod"/>
          <div class="center-xs bg-cl-secondary py20">
            <div class="col-sm-10 col-md-8 col-xs-12 mb20 centered-button">
              <div class="center-xs">
                <div class="col-xs-12 col-md-12 px15">
                  <slot name="placeOrderButton">
                    <button-full @click.native="changeMethodMode = false" class="place-order-btn">
                      {{ $t('Select') }}
                    </button-full>
                  </slot>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <p class="center-md center-xs">{{$t('You have to pay us ')}} <strong>{{ (amountToPay + ipayFee) | price }}</strong> {{($t('including fee'))}}</p>
          <template v-if="selectedMethod === 'ipay_masterpass'">
            <template v-if="!showOtp">
              <p class="center-md center-xs">{{$t('Please, select card to pay:')}}</p>
              <pay-order-modal-credit-cards-list @selected-card="afterSelectedCard" :order-id="order.id" />
              <div class="change-method" @click="changeMethodMode = true">
                {{ $t('Change payment method') }}
              </div>
              <div class="center-xs bg-cl-secondary py20">
                <div class="col-sm-10 col-md-8 col-xs-12 mb20 centered-button">
                  <div class="center-xs">
                    <div class="col-xs-12 col-md-12 px15">
                      <slot name="placeOrderButton">
                        <button-full @click.native="pay" class="place-order-btn" :disabled="payBlocked">
                          <img src="../../../../../assets/icons/icon-6.png" alt="Pay for order" width="22" height="15" loading="lazy" fetchpriority="auto"> {{ $t('Pay for order') }}
                        </button-full>
                      </slot>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="mb10">
                <base-input v-model="otpVerificationCode" name="code" type="text" focus :placeholder="$t('Verification Code *')" />
              </div>
              <button-full class="mb35" type="submit" :disabled="!otpVerificationCode || otpVerifying" @click.native="verify">
                {{ $t('Verify') }}
              </button-full>
            </template>
          </template>
          <template v-else-if="selectedMethod === 'ipay_gpay'">
            <div class="change-method" @click="changeMethodMode = true">
              {{ $t('Change payment method') }}
            </div>
            <div class="center-xs bg-cl-secondary py20">
              <div class="col-sm-10 col-md-8 col-xs-12 mb20 centered-button">
                <div class="center-xs">
                  <div class="col-xs-12 col-md-12 px15">
                    <google-pay-button :transaction-info="transactionInfo" @payed="payed" @cancel="cancelled" :on-click-validation="buttonValidation" />
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-else-if="selectedMethod === 'ipay_applepay'">
            <div class="change-method" @click="changeMethodMode = true">
              {{ $t('Change payment method') }}
            </div>
            <div class="center-xs bg-cl-secondary py20">
              <div class="col-sm-10 col-md-8 col-xs-12 mb20 centered-button">
                <div class="center-xs">
                  <div class="col-xs-12 col-md-12 px15">
                    <apple-pay-button @payed="payed"
                                      @cancel="cancelled"
                                      :totals="applePayTotals"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
        </template>
      </div>
    </div>
  </modal>
</template>

<script async>
import config from 'config'
import i18n from '@vue-storefront/i18n'
import { mapGetters, mapActions } from 'vuex'
import { isServer } from '@vue-storefront/core/helpers'
import { isOnlinePaymentMethod } from 'theme/helpers/paymentMethod'
import { notifyPaymentBeingChecked, notifyOrderAlreadyPaid, translatePaymentMessage } from 'src/modules/ipay-payment/helpers/paymentMessages'
import { otpVerificationOutcome, runOtpVerification } from 'src/modules/ipay-payment/helpers/otpVerification'
import { paymentCreateOutcome, ensurePaymentRecorded, unrecordedPaymentOutcome } from 'src/modules/ipay-payment/helpers/paymentRecording'

const Modal = () => import(/* webpackChunkName: "vsf-modal" */ 'theme/components/core/Modal');
const PayOrderModalCreditCardsList = () => import(/* webpackChunkName: "vsf-pay-order-modal-credit-cards-list" */ './PayOrderModalCreditCardsList');
const ButtonFull = () => import(/* webpackChunkName: "vsf-button-full" */ 'theme/components/theme/ButtonFull');
const BaseInput = () => import(/* webpackChunkName: "vsf-base-input" */ 'theme/components/core/blocks/Form/BaseInput.vue');
const GooglePayButton = () => import(/* webpackChunkName: "vsf-google-pay-button" */ 'src/modules/ipay-payment/components/GooglePayButton');
const ApplePayButton = () => import(/* webpackChunkName: "vsf-apple-pay-button" */ 'src/modules/ipay-payment/components/ApplePayButton');
const PayOrderModalChangeMethods = () => import(/* webpackChunkName: "vsf-pay-order-modal-change-methods" */ './PayOrderModalChangeMethods.vue');

export default {
  name: 'PayOrderModal',
  components: {
    Modal,
    PayOrderModalCreditCardsList,
    ButtonFull,
    BaseInput,
    GooglePayButton,
    ApplePayButton,
    PayOrderModalChangeMethods
  },
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters({
      cards: 'ipay-payment-user/getCards',
      getMessage: 'checkout/getPaymentFailedMessage',
      getCustomerIp: 'user/getCustomerIp',
      getCustomerUA: 'user/getCustomerUA',
      getCustomerAccept: 'user/getCustomerAccept',
      getPhoneNumber: 'user/getPhoneNumber',
      resetFee: 'ipay-payment-user/resetFee'
    }),
    amountToPay () {
      return this.order.grand_total - this.order.paid
    },
    modalName () {
      return `pay-order-${this.order.id}`
    },
    baseUrl () {
      return isServer ? 'https://www.korzyna.com' : window.location.origin
    },
    requestorUrl () {
      return isServer ? 'https://www.korzyna.com' : window.location.href
    },
    browserLanguage () {
      return isServer ? 'uk-UA' : window.navigator.language
    },
    browserDimensions () {
      return isServer ? { width: 500, height: 500 } : { width: window.innerWidth, height: window.innerHeight }
    },
    getCustomerId () {
      return this.$store.state.user.current.id
    },
    transactionInfo () {
      return {
        totalPriceStatus: 'FINAL',
        totalPrice: parseFloat(this.amountToPay + this.ipayFee).toString(),
        currencyCode: 'UAH',
        countryCode: 'UA'
      }
    },
    checkOnlinePayment () {
      return isOnlinePaymentMethod(this.selectedMethod)
    },
    applePayTotals () {
      return {
        total: parseFloat(this.amountToPay + this.ipayFee),
        subtotal: parseFloat(this.amountToPay),
        shipping: 0,
        fee: parseFloat(this.ipayFee)
      }
    },
    selectedMethod () {
      return this.method ? this.method : this.order.payment_method_code
    },
    // K2-3084: no second payment while one is being created, its OTP code is
    // checked or the bank's 3DS page loads.
    payBlocked () {
      return this.paying || this.redirectingTo3ds || this.otpVerifying
    }
  },
  data () {
    return {
      cardName: '',
      showOtp: false,
      otpPaymentToken: null,
      otpVerificationCode: '',
      otpVerifying: false,
      paying: false,
      redirectingTo3ds: false,
      ipayFee: 0,
      gpayToken: null,
      method: '',
      changeMethodMode: false
    }
  },
  async mounted () {
    this.$bus.$on('modal-show', this.onModalShow)
    window.addEventListener('pageshow', this.onPageShow)
    this.method = ''
    this.changeMethodMode = false
    if (this.checkOnlinePayment) {
      await this.calculateFee({
        phoneNumber: this.getPhoneNumber(true),
        customerId: this.getCustomerId,
        amount: (this.amountToPay * 100),
        method: this.selectedMethod.replace('ipay_', '')
      })
        .then(res => { this.ipayFee = ((res.result.amount - res.result.invoice) / 100)})
        .then(() => {
          this.$emit('calculateFee', this.ipayFee)
        })
    }
    if (this.selectedMethod === 'ipay_masterpass') {
      this.$bus.$on('ipay-selected-card', this.afterSelectedCard)
    }
  },
  methods: {
    ...mapActions({
      createPayment: 'ipay-payment-user/createPayment',
      createTransaction: 'ipay-payment-user/createTransaction',
      failedTransaction: 'ipay-payment-user/failedTransaction',
      toggleOtpModal: 'ipay-payment-user/toggleOtpModal',
      verify2DS: 'ipay-payment-user/verify2DS',
      updateOrderData: 'user/updateOrderLocally',
      calculateFee: 'ipay-payment-user/calculateFee'
    }),
    afterSelectedCard (name) {
      this.cardName = name
    },
    buttonValidation () {
      return true
    },
    cancelled () {
      this.$store.dispatch('notification/spawnNotification', {
        type: 'error',
        message: this.$t('You have canceled the payment'),
        action1: { label: this.$t('OK') }
      })
    },
    payed (paymentData) {
      if (paymentData && paymentData.paymentMethodData && paymentData.paymentMethodData.tokenizationData) {
        this.gpayToken = btoa(paymentData.paymentMethodData.tokenizationData.token)
        this.$bus.$emit('notification-progress-start', this.$t('Processing payments ...'))
        this.pay()
      }
      if (paymentData && paymentData.token) {
        this.gpayToken = btoa(JSON.stringify(paymentData.token))
        this.$bus.$emit('notification-progress-start', this.$t('Processing payments ...'))
        this.pay()
      }
    },
    async pay () {
      // K2-3084: one payment at a time (the wallets call this directly). A
      // running payment or OTP check stops the loader itself; otherwise (only
      // the 3DS redirect blocks) the one a wallet's payed() started is stopped.
      if (this.payBlocked) {
        if (!this.paying && !this.otpVerifying) {
          this.$bus.$emit('notification-progress-stop', {})
        }
        return
      }
      this.paying = true
      // K2-3073: the loader (started here or by payed()) stops on every exit -
      // but a 3DS redirect, where it stays on while the bank's page loads.
      try {
        return await this.createOrderPayment()
      } finally {
        this.paying = false
        if (!this.redirectingTo3ds) {
          this.$bus.$emit('notification-progress-stop', {})
        }
      }
    },
    async createOrderPayment () {
      if (this.selectedMethod === 'ipay_masterpass') {
        if (!this.cards.length) {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'error',
            message: this.$t('You have no added CCs'),
            action1: {label: this.$t('OK')}
          })
          return
        }
        if (!this.cardName) {
          this.$store.dispatch('notification/spawnNotification', {
            type: 'error',
            message: this.$t('You have no selected CC'),
            action1: {label: this.$t('OK')}
          })
          return
        }
      }
      this.$bus.$emit('notification-progress-start', this.$t('Processing payments ...'))
      let amount = parseInt((this.amountToPay.toFixed(2) * 100))
      let onlinePaymentPayload = {
        // K2-3073: vuestorefront-api charges what ms-api says this order owes.
        order_ids: [this.order.id],
        cardName: this.cardName,
        customerId: this.getCustomerId,
        phoneNumber: this.getPhoneNumber(true),
        amount,
        description: `Оплата замовлення # ${this.order.connection_order_id} на korzyna.com`,
        info: {},
        method: this.selectedMethod.replace('ipay_', ''),
        threeds_info: {
          notification_url: `${this.baseUrl}${config.ipay.payment.verify_threeds_endpoint}?order_id=${this.order.id}`,
          threeds_requestor_url: this.requestorUrl,
          browser_language: this.browserLanguage,
          browser_screen_height: this.browserDimensions.height,
          browser_screen_width: this.browserDimensions.width,
          browser_accept_header: this.getCustomerAccept,
          browser_tz: new Date().getTimezoneOffset(),
          browser_user_agent: this.getCustomerUA,
          user_ip: this.getCustomerIp
        }
      }
      if (['ipay_gpay', 'ipay_applepay'].includes(this.selectedMethod)) {
        onlinePaymentPayload.token = this.gpayToken
      }
      await this.createPayment(onlinePaymentPayload)
        .then(async res => {
          const created = paymentCreateOutcome(res)
          if (created === 'paid') {
            // K2-3073: already paid (by an earlier payment) - shown as paid, no Pay button.
            this.updateOrderData({ id: this.order.id, data: { is_paid: true } })
            this.order.is_paid = 1
            this.close()
            notifyOrderAlreadyPaid(this.$store, key => i18n.t(key))
            return null
          }
          if (created === 'pending') {
            // K2-3073: iPay may have the payment - never reported as failed.
            this.close()
            notifyPaymentBeingChecked(this.$store, key => i18n.t(key))
            return null
          }
          if (created === 'failed') {
            console.error(res)
            this.failedTransaction({ order_id: this.order.id })
            this.close()
            let message = res && res.message ? res.message : 'Something went wrong'
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: translatePaymentMessage(message, key => i18n.t(key)),
              action1: { label: i18n.t('OK') }
            })
            throw new Error(message)
          }
          const recorded = await ensurePaymentRecorded(res, [this.order], order => this.createTransaction({
            order_id: order.id,
            customer_id: this.getCustomerId,
            transaction_id: res.result.pmt_id,
            type: this.selectedMethod,
            transaction_data: res.result
          }))
          if (!recorded) {
            this.close()
            // A possibly charged payment is never shown or reported as failed.
            if (unrecordedPaymentOutcome(res.result) === 'pending') {
              notifyPaymentBeingChecked(this.$store, key => i18n.t(key))
            } else {
              this.$store.dispatch('notification/spawnNotification', {
                type: 'error',
                message: i18n.t('Something went wrong. Try again in a few seconds.'),
                action1: { label: i18n.t('OK') }
              })
            }
            return null
          }
          return res
        })
        .then(res => {
          if (!res) {
            return res
          }
          let result = res.result
          if (parseInt(result.pmt_status) === 5) {
            this.updateOrderData({id: this.order.id, data: { is_paid: true }})
            this.close()
            this.$store.dispatch('notification/spawnNotification', {
              type: 'success',
              message: i18n.t('Your payment successfully accepted!'),
              action1: { label: i18n.t('OK') }
            })
            this.order.is_paid = 1
            this.$router.push({ path: `${this.localizedRoute('/checkout/success')}/${this.order.location_code}/${this.order.connection_order_id}` });
            return res
          }
          if (result.security_rate === '3D' && result.security_data && result.security_data.redirect_url) {
            // K2-3084: Pay stays disabled until the page is left.
            this.redirectingTo3ds = true
            window.location.href = result.security_data.redirect_url
            return
          }
          this.$bus.$emit('notification-progress-stop', {})
          this.otpPaymentToken = result.security_data ? result.security_data.token : result.token
          this.confirmedOrderId = this.order.id
          this.showOtp = true
          return res
        })
    },
    verify () {
      // K2-3073: loader while the code is checked; a repeated submit is ignored.
      return runOtpVerification(this, this.$t('Processing payments ...'), () => this.verify2DS({
        customerId: this.getCustomerId,
        phoneNumber: this.getPhoneNumber(true),
        token: this.otpPaymentToken,
        value: this.otpVerificationCode,
        order_id: this.order.id
      })
        .then(async res => {
          const outcome = otpVerificationOutcome(res)
          if (outcome === 'pending') {
            // Possibly charged (202): never reported or shown as failed.
            this.close()
            notifyPaymentBeingChecked(this.$store, key => i18n.t(key))
            return
          }
          if (outcome === 'wrong_code') {
            console.error(res)
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: i18n.t('SMS code value incorrect!'),
              action1: { label: i18n.t('OK') }
            })
            this.failedTransaction({ order_id: this.order.id})
            this.close()
            return
          }
          if (outcome === 'failed') {
            console.error(res)
            this.$store.dispatch('notification/spawnNotification', {
              type: 'error',
              message: translatePaymentMessage(res.message ? res.message : res.result, key => i18n.t(key)),
              action1: { label: i18n.t('OK') }
            })
            this.failedTransaction({ order_id: this.order.id})
            this.close()
            return
          }
          this.showOtp = false
          this.updateOrderData({ id: this.order.id, data: { is_paid: true } })
          this.close()
          this.$store.dispatch('notification/spawnNotification', {
            type: 'success',
            message: i18n.t('Your payment successfully accepted!'),
            action1: { label: i18n.t('OK') }
          })
          this.order.is_paid = 1
          this.$router.push({ path: `${this.localizedRoute('/checkout/success')}/${this.order.location_code}/${this.order.connection_order_id}` });
        })
        .catch(err => {
          // No answer: the confirmation may have gone through - never shown as failed.
          console.error(err)
          this.close()
          notifyPaymentBeingChecked(this.$store, key => i18n.t(key))
        }))
    },
    close () {
      this.showOtp = false
      this.$bus.$emit('modal-hide', this.modalName)
      this.method = ''
      this.changeMethodMode = false
    },
    changeMethod (method) {
      this.method = method
    },
    onModalShow (name) {
      // K2-3073: every (re)open starts with an empty OTP form. K2-3084: but
      // not while a code is being checked - Verify stays disabled until it ends.
      if (name === this.modalName && !this.otpVerifying) {
        this.showOtp = false
        this.otpVerificationCode = ''
      }
    },
    onPageShow (event) {
      // K2-3084: back from the bank's 3DS page to a page restored from the
      // browser's cache - the redirect is over.
      if (event && event.persisted && this.redirectingTo3ds) {
        this.redirectingTo3ds = false
        this.$bus.$emit('notification-progress-stop', {})
      }
    }
  },
  beforeDestroy () {
    window.removeEventListener('pageshow', this.onPageShow)
    this.$bus.$off('modal-show', this.onModalShow)
    this.$bus.$off('ipay-selected-card', this.afterSelectedCard)
  }
}
</script>

<style lang="scss" scoped>
  .centered-button {
    margin: 0 auto;
  }

  @media only screen and (max-width: 600px) {
    .modal-header span {
      margin-top: 20px;
    }
  }
  .change-method {
    text-align: center;
    text-decoration: underline;
    color: #11bb2e;
    margin: 5px 0 20px;
  }
</style>
